import { useState } from "react";
import {
  Key,
  Plus,
  MoreHorizontal,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  AlertTriangle,
  Ban,
  LayoutGrid,
  List,
  RefreshCw,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useApiKeysList,
  useCreateApiKeyMutation,
  useRevokeApiKey,
  useReactivateApiKey,
  useDeleteApiKeyMutation,
} from "@/hooks/useQueries";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

// API key card component for grid view
const APIKeyCard = ({ apiKey, onRevoke, onReactivate, onDelete }) => {
  const { t } = useTranslation();
  const isRevoked =
    apiKey.revokedAt ||
    apiKey.revoked_at ||
    apiKey.status === "revoked" ||
    apiKey.isActive === false ||
    apiKey.is_active === false;

  return (
    <Card
      className={`group overflow-hidden hover:shadow-md transition-shadow ${
        isRevoked ? "opacity-60" : ""
      }`}
    >
      <div
        className={`h-24 flex items-center justify-center relative ${
          isRevoked ? "bg-muted" : "bg-primary/10"
        }`}
      >
        <Key
          className={`h-8 w-8 ${
            isRevoked ? "text-muted-foreground" : "text-primary"
          }`}
        />

        <div className="absolute top-2 right-2 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isRevoked ? (
                <DropdownMenuItem onClick={() => onReactivate(apiKey)}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("apiKeys.reactivate")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onRevoke(apiKey)}>
                  <Ban className="mr-2 h-4 w-4" />
                  {t("apiKeys.revoke")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(apiKey)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("common.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CardContent className="p-4 dark:bg-background">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium truncate">{apiKey.name}</h3>
          {isRevoked ? (
            <Badge
              variant="secondary"
              className="shrink-0 text-xs bg-slate-500 text-white"
            >
              {t("apiKeys.statusRevoked")}
            </Badge>
          ) : (
            <Badge className="shrink-0 text-xs bg-emerald-500 hover:bg-emerald-500">
              {t("apiKeys.statusActive")}
            </Badge>
          )}
        </div>

        <code className="text-xs font-mono text-muted-foreground">
          {apiKey.keyPrefix || apiKey.key_prefix}••••••••
        </code>

        <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
          <span>
            Created{" "}
            {formatDateShort(apiKey.createdAt, useAuthStore.getState().user)}
          </span>
          {apiKey.lastUsedAt && (
            <span>
              Used{" "}
              {formatDateShort(apiKey.lastUsedAt, useAuthStore.getState().user)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// API key item component (table row)
const APIKeyItem = ({ apiKey, onRevoke, onReactivate, onDelete }) => {
  const { t } = useTranslation();
  const [showKey, setShowKey] = useState(false);

  const copyToClipboard = () => {
    if (apiKey.key) {
      navigator.clipboard.writeText(apiKey.key);
      toast.success(t("apiKeys.copied"));
    }
  };

  const maskedKey = apiKey.key
    ? `${apiKey.key.slice(0, 8)}${"•".repeat(24)}${apiKey.key.slice(-4)}`
    : "••••••••••••••••••••••••";

  const isRevoked =
    apiKey.revokedAt ||
    apiKey.revoked_at ||
    apiKey.status === "revoked" ||
    apiKey.isActive === false ||
    apiKey.is_active === false;

  return (
    <TableRow className={isRevoked ? "opacity-60" : ""}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              isRevoked ? "bg-muted" : "bg-primary/10"
            }`}
          >
            <Key
              className={`h-4 w-4 ${
                isRevoked ? "text-muted-foreground" : "text-primary"
              }`}
            />
          </div>
          <span className="font-medium">{apiKey.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <code className="text-xs font-mono text-muted-foreground">
            {showKey && apiKey.key ? apiKey.key : maskedKey}
          </code>
          {apiKey.key && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? (
                  <EyeOff className="h-3 w-3" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={copyToClipboard}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
      <TableCell>
        {isRevoked ? (
          <Badge variant="secondary" className="bg-slate-500 text-white">
            {t("apiKeys.statusRevoked")}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="bg-emerald-500/10 text-emerald-600 border-emerald-200"
          >
            {t("apiKeys.statusActive")}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {formatDateShort(apiKey.createdAt, useAuthStore.getState().user)}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {apiKey.lastUsedAt
          ? formatDateShort(apiKey.lastUsedAt, useAuthStore.getState().user)
          : "-"}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isRevoked ? (
              <DropdownMenuItem onClick={() => onReactivate(apiKey)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("apiKeys.reactivate")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onRevoke(apiKey)}>
                <Ban className="mr-2 h-4 w-4" />
                {t("apiKeys.revoke")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(apiKey)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("common.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

// New key display component
const NewKeyDisplay = ({ apiKey, onClose }) => {
  const { t } = useTranslation();
  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success("API key copied to clipboard");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apiKeys.created")}</DialogTitle>
          <DialogDescription>
            Copy your API key now. You won't be able to see it again.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
            <code className="flex-1 text-sm font-mono break-all">{apiKey}</code>
            <Button variant="outline" size="sm" onClick={copyToClipboard}>
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </div>
          <div className="flex items-start gap-2 mt-4 p-3 bg-orange-100 rounded-lg border border-orange-300">
            <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
            <p className="text-sm text-gray-900 font-medium">
              Make sure to copy this key now. For security reasons, it won't be
              shown again.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t("common.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Loading skeleton
const APIKeysSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
    </div>
    <Card>
      <CardContent className="p-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </CardContent>
    </Card>
  </div>
);

// Empty state
const EmptyState = ({ onCreateClick }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Key className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">{t("apiKeys.noKeys")}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t("apiKeys.noKeysDesc")}
        </p>
        <Button onClick={onCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          {t("apiKeys.createKey")}
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("apiKeys.title", "API Keys")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "apiKeys.subtitle",
            "Manage your API keys for programmatic access",
          )}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.apiKeysUpgradeDesc",
              "API keys allow you to integrate eScribe with your applications and automate document workflows.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Key className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("apiKeys.withApiKeysYouCan", "With API Keys you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "apiKeys.feature1",
                  "Integrate with your applications programmatically",
                )}
              </li>
              <li>
                {t(
                  "apiKeys.feature2",
                  "Automate document creation and signing workflows",
                )}
              </li>
              <li>
                {t(
                  "apiKeys.feature3",
                  "Build custom integrations with fine-grained permissions",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const APIKeys = () => {
  const { t } = useTranslation();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has API keys feature - must be explicitly true
  const hasApiKeysFeature = user?.subscription?.features?.apiKeys === true;

  // Local UI state only
  const [viewMode, setViewMode] = useState("grid");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [keyScopes, setKeyScopes] = useState({
    read: true,
    write: true,
    delete: false,
  });

  // ============================================
  // TanStack Query - Data fetching (only if feature enabled)
  // ============================================
  const { data: apiKeys = [], isLoading } = useApiKeysList({
    enabled: hasApiKeysFeature,
  });

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const createApiKey = useCreateApiKeyMutation();
  const revokeApiKey = useRevokeApiKey();
  const reactivateApiKey = useReactivateApiKey();
  const deleteApiKey = useDeleteApiKeyMutation();

  // ============================================
  // Event Handlers
  // ============================================
  const resetForm = () => {
    setKeyName("");
    setKeyScopes({ read: true, write: true, delete: false });
    setSelectedKey(null);
  };

  const handleCreateKey = (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;

    const scopes = Object.entries(keyScopes)
      .filter(([, enabled]) => enabled)
      .map(([scope]) => scope);

    createApiKey.mutate(
      { name: keyName, scopes },
      {
        onSuccess: (data) => {
          setNewKey(data?.key);
          setShowCreateDialog(false);
          resetForm();
        },
      },
    );
  };

  const handleRevokeKey = () => {
    if (!selectedKey) return;

    revokeApiKey.mutate(selectedKey.id, {
      onSuccess: () => {
        setShowRevokeDialog(false);
        setSelectedKey(null);
      },
    });
  };

  const handleReactivateKey = () => {
    if (!selectedKey) return;

    reactivateApiKey.mutate(selectedKey.id, {
      onSuccess: () => {
        setShowReactivateDialog(false);
        setSelectedKey(null);
      },
    });
  };

  const handleDeleteKey = () => {
    if (!selectedKey) return;

    deleteApiKey.mutate(selectedKey.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        setSelectedKey(null);
      },
    });
  };

  const openRevokeDialog = (key) => {
    setSelectedKey(key);
    setShowRevokeDialog(true);
  };

  const openReactivateDialog = (key) => {
    setSelectedKey(key);
    setShowReactivateDialog(true);
  };

  const openDeleteDialog = (key) => {
    setSelectedKey(key);
    setShowDeleteDialog(true);
  };

  // ============================================
  // Render
  // ============================================

  // ============================================
  // Feature Check - Show upgrade message if API keys not available
  // ============================================

  if (!hasApiKeysFeature) {
    return <UpgradeRequired />;
  }

  if (isLoading) {
    return <APIKeysSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("apiKeys.title")}
          </h1>
          <p className="text-muted-foreground">{t("apiKeys.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("apiKeys.createKey")}
          </Button>
        </div>
      </div>

      {/* API Keys grid/list */}
      {apiKeys.length === 0 ? (
        <EmptyState onCreateClick={() => setShowCreateDialog(true)} />
      ) : (
        <>
          {/* Info card */}
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                API keys allow you to access the eScribe API programmatically.
                Keep your keys secure and never share them publicly. See the{" "}
                <a
                  href="/api-docs.html"
                  target="_blank"
                  className="text-primary hover:underline"
                >
                  API documentation
                </a>{" "}
                for usage details.
              </p>
            </CardContent>
          </Card>

          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {apiKeys.map((key) => (
                <APIKeyCard
                  key={key.id}
                  apiKey={key}
                  onRevoke={openRevokeDialog}
                  onReactivate={openReactivateDialog}
                  onDelete={openDeleteDialog}
                />
              ))}
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("apiKeys.key")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("common.created")}</TableHead>
                    <TableHead>{t("apiKeys.lastUsed")}</TableHead>
                    <TableHead className="w-12.5" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <APIKeyItem
                      key={key.id}
                      apiKey={key}
                      onRevoke={openRevokeDialog}
                      onReactivate={openReactivateDialog}
                      onDelete={openDeleteDialog}
                    />
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      {/* New Key Display */}
      {newKey && (
        <NewKeyDisplay apiKey={newKey} onClose={() => setNewKey(null)} />
      )}

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiKeys.createKey")}</DialogTitle>
            <DialogDescription>{t("apiKeys.createKeyDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateKey}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="keyName">{t("common.name")}</Label>
                <Input
                  id="keyName"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder={t("apiKeys.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("apiKeys.scopes")}</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="scopeRead"
                      checked={keyScopes.read}
                      onCheckedChange={(checked) =>
                        setKeyScopes({ ...keyScopes, read: checked })
                      }
                    />
                    <Label htmlFor="scopeRead" className="text-sm font-normal">
                      {t("apiKeys.scopeRead")}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="scopeWrite"
                      checked={keyScopes.write}
                      onCheckedChange={(checked) =>
                        setKeyScopes({ ...keyScopes, write: checked })
                      }
                    />
                    <Label htmlFor="scopeWrite" className="text-sm font-normal">
                      {t("apiKeys.scopeWrite")}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="scopeDelete"
                      checked={keyScopes.delete}
                      onCheckedChange={(checked) =>
                        setKeyScopes({ ...keyScopes, delete: checked })
                      }
                    />
                    <Label
                      htmlFor="scopeDelete"
                      className="text-sm font-normal"
                    >
                      {t("apiKeys.scopeDelete")}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createApiKey.isPending || !keyName.trim()}
              >
                {createApiKey.isPending
                  ? t("common.loading")
                  : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revoke Key Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiKeys.revokeKey")}</DialogTitle>
            <DialogDescription>{t("apiKeys.revokeKeyDesc")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">{selectedKey?.name}</p>
            <code className="text-xs font-mono text-muted-foreground">
              {selectedKey?.keyPrefix || selectedKey?.key_prefix}••••••••
            </code>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRevokeDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevokeKey}
              disabled={revokeApiKey.isPending}
            >
              {revokeApiKey.isPending
                ? t("common.loading")
                : t("apiKeys.revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Key Dialog */}
      <Dialog
        open={showReactivateDialog}
        onOpenChange={setShowReactivateDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiKeys.reactivateKey")}</DialogTitle>
            <DialogDescription>
              {t("apiKeys.reactivateKeyDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">{selectedKey?.name}</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReactivateDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleReactivateKey}
              disabled={reactivateApiKey.isPending}
            >
              {reactivateApiKey.isPending
                ? t("common.loading")
                : t("apiKeys.reactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Key Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiKeys.deleteKey")}</DialogTitle>
            <DialogDescription>{t("apiKeys.deleteKeyDesc")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">{selectedKey?.name}</p>
            <p className="text-sm text-muted-foreground mt-1">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteKey}
              disabled={deleteApiKey.isPending}
            >
              {deleteApiKey.isPending
                ? t("common.loading")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APIKeys;
